import { Button, Stack, Switch, Typography } from "@mui/material";
import { useSetProfileVisibilityMutation } from "Api/Mutations/Social/useSetProfileVisibilityMutation";
import { useFollowRequestSummaryQuery } from "Api/Queries/Social/useFollowRequestsQuery";
import { ConfirmDialog } from "Components/Shared/Dialogs/ConfirmDialog";
import { useConfirmDialog } from "Hooks/useConfirmDialog";
import { type UserInfoQueryResult, Version } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Social.PublicPermissions;

/** Input id — lets host labels (htmlFor) toggle the switch natively. */
export const PROFILE_VISIBILITY_SWITCH_ID = "profile-visibility-switch";

type Props = Pick<UserInfoQueryResult, "isProfilePublic" | "version">;

/**
 * The public/private switch once the persona's user info is known (ProfileVisibilitySwitch gates
 * on it). Only a completed registration (FullMoone) may opt into public, so the switch stays
 * disabled for other versions (the backend enforces the same rule). Optimistic save. Going public
 * approves every pending follow request (Rules v2, FO7), so when any are pending the switch
 * confirms first, naming how many become mutual followers — and it stays disabled until those
 * counts have loaded. When they cannot be loaded the switch says so and offers a retry instead of
 * sitting disabled with no explanation.
 */
export const ProfileVisibilityToggle = ({
  isProfilePublic,
  version,
}: Props) => {
  const { t } = useResource();
  const { mutate: setVisibility } = useSetProfileVisibilityMutation();
  const { isOpen, open, handleResult } = useConfirmDialog();

  const canGoPublic = version === Version.FullMoone;
  const isPrivate = !isProfilePublic;
  const {
    data: followRequestSummary,
    isError: isFollowRequestSummaryError,
    isFetching: isFollowRequestSummaryFetching,
    refetch: refetchFollowRequestSummary,
  } = useFollowRequestSummaryQuery(canGoPublic && isPrivate);
  // Going public must never skip the confirm, so a private profile can't flip until the counts
  // it needs are in — fail closed while they load (or fail and retry).
  const isAwaitingFollowRequestSummary = isPrivate && !followRequestSummary;

  const handleChange = (isPublic: boolean) => {
    if (isPublic && followRequestSummary?.pendingIncomingCount) {
      open();
      return;
    }
    setVisibility({ isPublic });
  };

  return (
    <>
      <Switch
        size="small"
        checked={isProfilePublic}
        disabled={!canGoPublic || isAwaitingFollowRequestSummary}
        onChange={(e) => handleChange(e.target.checked)}
        slotProps={{
          input: {
            "aria-label": t(RESOURCES.PublicProfileLabel),
            id: PROFILE_VISIBILITY_SWITCH_ID,
          },
        }}
      />
      {canGoPublic && isFollowRequestSummaryError && (
        <Stack direction="row" alignItems="center" gap={1} width="100%">
          <Typography variant="caption" color="error">
            {t(RESOURCES.GoPublicSummaryUnavailable)}
          </Typography>
          <Button
            size="small"
            variant="text"
            disabled={isFollowRequestSummaryFetching}
            onClick={() => refetchFollowRequestSummary()}
          >
            {t(Resources.Common.Retry)}
          </Button>
        </Stack>
      )}
      <ConfirmDialog
        isOpen={isOpen}
        modalTitle={t(RESOURCES.GoPublicConfirmTitle)}
        modalContent={
          <Stack gap={1}>
            <Typography>
              {t(RESOURCES.GoPublicConfirmRequests, {
                count: followRequestSummary?.pendingIncomingCount ?? 0,
              })}
            </Typography>
            {!!followRequestSummary?.mutualOnApprovalCount && (
              <Typography>
                {t(RESOURCES.GoPublicConfirmMutual, {
                  count: followRequestSummary.mutualOnApprovalCount,
                })}
              </Typography>
            )}
          </Stack>
        }
        confirmButtonText={t(RESOURCES.GoPublicConfirmAction)}
        handleResult={handleResult(() => setVisibility({ isPublic: true }))}
      />
    </>
  );
};
